import { CheckboxFilter } from '~/components/BettingInsights/components/Filters/CheckboxFilter';
import { DateRefinement } from '~/components/BettingInsights/components/Filters/DateRefinement';
import { SliderFilter } from '~/components/BettingInsights/components/Filters/SliderFilter';
import { ResultFooter } from '~/components/BettingInsights/components/ResultsCard/ResultFooter';
import ResultCardStyles from '~/components/BettingInsights/components/ResultsCard/styles/ResultCard.module.css';
import { positiveEvQueryFactory } from '~/components/BettingInsights/queries/positiveEvQueryFactory';
import { trackEvBetClick } from '~/components/BettingInsights/tracking';
import {
  aggregateBetsFromResponse,
  type ValueBetSearchResponseBody } from
'~/components/BettingInsights/utils/aggregateBetsFromResponse';
import { Avatar } from '~/components/OddscheckerPlus/components/Avatar';
import { BetButton } from '~/components/OddscheckerPlus/components/BetButton';
import { Progress } from '~/components/OddscheckerPlus/components/cxy';
import { featureFlags } from '~/providers/feature-flag-provider';
import { type Loggable } from '~/traits/Loggable';
import { type RequestContext } from '~/types/RequestContext';

import { formatDate } from './utils/formatDate';
import {
  BettingInsights,
  supportedCategoryGroupsIds,
  wnbaBetTypeIds,
  wnbaEventId } from
'./BettingInsightsConfig';
import { type PositiveEvIndex } from './types';

export const positiveEvIndex = 'search-ev-bets';

export function positiveEvConfigFactory(
this: Loggable,
requestContext: RequestContext)
{
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const context = this;
  return BettingInsights.withRequestContext<PositiveEvIndex>(requestContext, {
    elasticSearch: {
      facetAttributes: [
      { attribute: 'aiProbability', field: 'aiProbability', type: 'numeric' },
      {
        attribute: 'eventDisplayName',
        field: 'eventDisplayName',
        type: 'string'
      },
      { attribute: 'eventId', field: 'eventId', type: 'numeric' },
      { attribute: 'expectedValue', field: 'expectedValue', type: 'numeric' },
      {
        attribute: 'marketPropType',
        field: 'marketPropType',
        type: 'string'
      },
      { attribute: 'oddsDecimal', field: 'oddsDecimal', type: 'numeric' },
      {
        attribute: 'subeventStartDate',
        field: 'subeventStartDate',
        type: 'string'
      },
      {
        attribute: 'subeventStartTime',
        field: 'subeventStartTime',
        type: 'string'
      },
      {
        attribute: 'sportsData.historyCount',
        field: 'sportsData.historyCount',
        type: 'numeric'
      },
      {
        attribute: 'sportsData.hitRates.last10',
        field: 'sportsData.hitRates.last10',
        type: 'numeric'
      }],

      getBaseFilters() {
        // Temp addition to toggle filtering out certain markets of WNBA
        const allWNBA = this.featureFlags.find(
          (flag) => flag.name === featureFlags.SUBS__PLUS__ALL_WNBA
        );

        const baseFilters = [
        {
          range: {
            expectedValue: {
              gte: 0
            }
          }
        },
        ...(allWNBA?.enabled ?
        [] :
        [
        {
          bool: {
            must_not: [
            {
              bool: {
                must: [
                { term: { eventId: wnbaEventId } },
                {
                  terms: {
                    betTypeId: wnbaBetTypeIds
                  }
                }]

              }
            }]

          }
        }])];



        const newCategoryGroupsFeature = this.featureFlags.find(
          (flag) => flag.name === featureFlags.SUBS__PLUS__NEW_CATEGORY_GROUPS
        );

        if (newCategoryGroupsFeature?.enabled) return baseFilters;

        return [
        ...baseFilters,
        {
          bool: {
            should: supportedCategoryGroupsIds.map((categoryGroupId) => ({
              term: {
                categoryGroupId
              }
            }))
          }
        }];

      },
      async beforeSearch(searchRequests) {
        const queryFactory = positiveEvQueryFactory({
          sorting: this.sorting?.()
        });
        context.logger.debug('[positive-ev]: beforeSearch', {
          query: queryFactory(searchRequests[0])
        });

        return searchRequests.map(queryFactory);
      },
      async afterSearch(_, searchResponses) {
        return await Promise.all(
          searchResponses.map((searchResponse) =>
          aggregateBetsFromResponse.call(
            context,
            this,
            searchResponse as ValueBetSearchResponseBody
          )
          )
        );
      },
      results: {
        attributes: [
        'aiProbability',
        'betDescription',
        'betId',
        'betName',
        'betTypeId',
        'bookmakerCode',
        'categoryGroupId',
        'categoryGroupName',
        'categoryId',
        'categoryName',
        'eventDisplayName',
        'eventId',
        'eventDisplayName',
        'expectedValue',
        'expectedValueStatus',
        'line',
        'marketClassification',
        'marketDisplayName',
        'marketDisplayNameMap',
        'marketId',
        'marketName',
        'marketPropType',
        'oddsAmerican',
        'oddsDecimal',
        'oddsFractional',
        'parentBetId',
        'sportsData',
        'sportsbookProbability',
        'subeventId',
        'subeventName',
        'subeventStartDate',
        'subeventStartTime',
        'teamData']

      },
      sorting() {
        return [
        // Set first in array as "default"
        {
          field: 'expectedValue',
          label: 'Expected Value High-Low',
          order: 'desc'
        },
        {
          field: 'expectedValue',
          label: 'Expected Value Low-High',
          order: 'asc'
        },
        {
          field: 'aiProbability',
          label: 'AI Prob High-Low',
          order: 'desc'
        },
        {
          field: 'aiProbability',
          label: 'AI Prob Low-High',
          order: 'asc'
        },
        {
          field: 'sportsbookProbability',
          label: 'Im. Prob High-Low',
          order: 'desc'
        },
        {
          field: 'sportsbookProbability',
          label: 'Im. Prob Low-High',
          order: 'asc'
        },
        {
          field: 'subeventStartTime',
          label: 'Event Time',
          order: 'asc'
        },
        {
          field: 'oddsDecimal',
          label: 'Best Odds High-Low',
          order: 'desc'
        },
        {
          field: 'oddsDecimal',
          label: 'Best Odds Low-High',
          order: 'asc'
        },

        {
          field: 'sportsData.hitRates.last10',
          label: 'Hit Rate (Last 10) High-Low',
          order: 'desc'
        },
        {
          field: 'sportsData.hitRates.last10',
          label: 'Hit Rate (Last 10) Low-High',
          order: 'asc'
        }];

      }
    },
    ui: {
      card: {
        table: {
          caption: {
            render({ content }) {
              return (
                <>
									<p data-testid="event-name">{content.subeventName}</p>
									<p
                    className={ResultCardStyles.headingDate}
                    data-testid="event-time">

										{formatDate(content.subeventStartTime)}
									</p>
								</>);

            }
          },
          footer: {
            render({ content }) {
              return <ResultFooter data={content} />;
            }
          },
          getContent({ result }) {
            return [result];
          },
          headings: [
          {
            key: 'aiProbability',
            render() {
              return 'AI Probability';
            },
            renderMobile() {
              return 'AI Prob';
            },
            tooltipText:
            'The % likelihood a projection will cover the consensus line based on our proprietary AI platform'
          },
          {
            key: 'sportsbookProbability',
            render() {
              return 'Implied Probability';
            },
            renderMobile() {
              return 'Im. Prob';
            },
            tooltipText:
            'Implied probability the bet will win based on the sportsbook with best odds'
          },
          {
            key: 'expectedValue',
            render() {
              return 'EV';
            },
            tooltipPosition: 'left',
            tooltipText:
            'The average ROI a bettor can expect for this bet based on our AI predictive model. >0% - Profitable Bet, <0% = Unprofitable Bet, >10% = Highly Profitable Bet',
            highlight: true,
            justifyContent: 'center'
          },
          {
            key: 'bestOdds',
            render() {
              return 'Best Odds';
            },
            mobileView: 'hide',
            justifyContent: 'center'
          }],

          main: {
            render({ content }) {
              return (
                <>
									<Avatar
                    betName={content.betName}
                    marketClassification={content.marketClassification}
                    marketPropType={content.marketPropType}
                    sportsData={content.sportsData}
                    teamData={content.teamData} />

									<div className={ResultCardStyles.titleCell} id="bet-name">
										{content.line ?
                    <>
												<h3
                        className={ResultCardStyles.titleHeading}
                        data-testid="bet-name">

													{content.line.name}
												</h3>
												<p
                        className={ResultCardStyles.titleText}
                        data-testid="bet-market">

													{content.line.sign} {content.line.absolute}{' '}
													{content.marketDisplayNameMap.us}
												</p>
											</> :

                    <>
												<h3
                        className={ResultCardStyles.titleHeading}
                        data-testid="bet-name">

													{content.betName}
												</h3>
												<p
                        className={ResultCardStyles.titleText}
                        data-testid="bet-market">

													{content.marketDisplayNameMap.us}
												</p>
											</>
                    }
									</div>
								</>);

            }
          },
          cells: [
          {
            key: 'aiProbability',
            render({ content, resultIndex: _ }) {
              return (
                <Progress
                  aria-label="AI Probability"
                  value={Math.round(content.aiProbability * 100) / 100} />);


            },
            alignItems: 'flex-end'
          },
          {
            key: 'sportsbookProbability',
            render({ content }) {
              return (
                <Progress
                  aria-label="Implied Probability"
                  value={
                  Math.round(content.sportsbookProbability * 100) / 100
                  } />);


            },
            alignItems: 'flex-end'
          },
          {
            key: 'expectedValue',
            render({ content }) {
              return <>{content.expectedValue}%</>;
            },
            highlight: ({ content }) =>
            content.expectedValueStatus?.toLowerCase(),
            justifyContent: 'center'
          },
          {
            key: 'bestOdds',
            render({ bookmakers, content }) {
              const bookmaker = bookmakers[content.bookmakerCode];
              const onClick = () => {
                trackEvBetClick({ bookmaker, result: content });
              };

              return (
                <BetButton
                  aria-label={`Place a bet on ${content.betName} ${content.marketDisplayNameMap.us}
										with ${bookmaker.bookmakerName} at odds of ${content.oddsAmerican}`}
                  bet={content}
                  bookieColour={bookmaker.brandColour || 'black'}
                  bookieLogo={bookmaker.logoSmall}
                  bookieName={bookmaker.bookmakerName}
                  odds={content.oddsAmerican}
                  onClick={onClick}
                  withLabel />);


            },
            isFullWidthMobile: true,
            justifyContent: 'center'
          }]

        }
      },
      initialUiState() {
        return {
          hitsPerPage: 10,
          range: {
            aiProbability: '30:100'
            // expectedValue: '0:100',
          },
          refinementList: {
            eventDisplayName: ['NFL']
          }
        };
      },
      refinements: [
      {
        attribute: 'expectedValue',
        label: 'Minimum EV',
        render() {
          return (
            <SliderFilter
              attribute={this.attribute}
              data-analytics="minimumEv"
              isDisabled={this.isWalled}
              label="EV"
              max={200} />);


        }
      },
      {
        attribute: 'aiProbability',
        label: 'Minimum AI Probability',
        render() {
          return (
            <SliderFilter
              attribute={this.attribute}
              data-analytics="minimumAi"
              isDisabled={this.isWalled}
              label="AI Probability"
              max={95}
              min={1} />);


        }
      },
      {
        attribute: 'sportsData.hitRates.last10',
        label: 'Minimum Hit Rate (Last 10)',
        render() {
          return (
            <SliderFilter
              attribute={this.attribute}
              data-analytics="minimumHitRate"
              isDisabled={this.isWalled}
              label="Hit Rate (Last 10)"
              max={100}
              step={10} />);


        }
      },
      {
        attribute: 'subeventStartDate',
        label: 'Dates',
        render() {
          return (
            <DateRefinement
              attribute={this.attribute}
              isDisabled={this.isWalled}
              label={this.label} />);


        }
      },
      {
        attribute: 'marketPropType',
        label: 'Bets',
        render() {
          return (
            <CheckboxFilter
              attribute={this.attribute}
              isDisabled={this.isWalled}
              label={this.label}
              sortBy={['name:asc']}
              transformFieldLabels={(name) => name.replace('Props', this.label)} />);


        }
      }],

      results: {
        categories: {
          attribute: 'eventDisplayName',
          order: [
          'NFL',
          'NBA',
          'NCAAF',
          'NCAAB',
          'MLB',
          'WNBA',
          'EPL',
          'MLS',
          'NHL']

        },
        labelledBy: '#bet-name',
        propsName: 'bets'
      },
      sorting: {
        canSort() {
          return true;
        }
      }
    }
  });
}